import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

const ContactContainer = styled.div`
  padding: 80px 0;
  background: linear-gradient(
    135deg,
    rgba(0, 191, 255, 0.03) 0%,
    rgba(30, 144, 255, 0.08) 50%,
    rgba(0, 191, 255, 0.05) 100%
  );
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at 20% 30%,
        rgba(0, 191, 255, 0.08) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 80% 70%,
        rgba(30, 144, 255, 0.06) 0%,
        transparent 50%
      );
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-primary);
  text-align: center;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
  
  &.animate {
    animation: ${fadeInUp} 0.8s ease-out;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: var(--gradient-primary);
    border-radius: 2px;
    opacity: 0;
    transition: opacity 0.6s ease 0.3s;
  }

  &.animate::after {
    opacity: 1;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  text-align: center;
  max-width: 600px;
  margin: 0 auto 3rem;
  line-height: 1.6;
  position: relative;
  z-index: 2;
  
  &.animate {
    animation: ${fadeInUp} 0.8s ease-out 0.2s both;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContactInfo = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 24px;
  padding: 3rem;
  border: 1px solid rgba(0, 191, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 191, 255, 0.1);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  transition: all 0.4s ease;
  
  &.animate {
    animation: ${slideInLeft} 0.8s ease-out 0.4s both;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
    transform: scaleX(0);
    transition: transform 0.6s ease 0.6s;
  }

  &.animate::before {
    transform: scaleX(1);
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 191, 255, 0.15);
    border-color: rgba(0, 191, 255, 0.2);
  }

  @media (max-width: 768px) {
    padding: 2rem;
    border-radius: 20px;
  }
`;

const ContactForm = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 24px;
  padding: 3rem;
  border: 1px solid rgba(0, 191, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 191, 255, 0.1);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  transition: all 0.4s ease;
  
  &.animate {
    animation: ${slideInRight} 0.8s ease-out 0.4s both;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
    transform: scaleX(0);
    transition: transform 0.6s ease 0.8s;
  }

  &.animate::before {
    transform: scaleX(1);
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 191, 255, 0.15);
    border-color: rgba(0, 191, 255, 0.2);
  }

  @media (max-width: 768px) {
    padding: 2rem;
    border-radius: 20px;
  }
`;

const InfoTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 2rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 2rem;
  gap: 1rem;
`;

const InfoIcon = styled.div`
  width: 56px;
  height: 56px;
  background: var(--gradient-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.4rem;
  flex-shrink: 0;
  box-shadow: 0 4px 20px rgba(0, 191, 255, 0.3);
  position: relative;
  transition: all 0.3s ease;
  
  font-size: 1.2rem;
  
  &::before {
    content: "";
    position: absolute;
    inset: -3px;
    background: var(--gradient-primary);
    border-radius: 50%;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: scale(1.1);
    animation: ${pulse} 2s infinite;
    
    &::before {
      opacity: 0.3;
    }
  }
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const InfoText = styled.div`
  color: var(--text-secondary);
  line-height: 1.5;
  
  a {
    color: var(--primary-blue);
    text-decoration: none;
    transition: all 0.3s ease;
    
    &:hover {
      color: var(--secondary-blue);
      text-decoration: underline;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: var(--text-primary);
`;

const InputWrapper = styled.div`
  position: relative;
  
  &.focused .floating-label {
    transform: translateY(-28px) scale(0.85);
    color: var(--primary-blue);
  }
  
  &.has-value .floating-label {
    transform: translateY(-28px) scale(0.85);
  }
`;

const FloatingLabel = styled.label`
  position: absolute;
  left: 1rem;
  top: 1rem;
  color: var(--text-secondary);
  font-weight: 500;
  transition: all 0.3s ease;
  pointer-events: none;
  background: white;
  padding: 0 0.5rem;
  z-index: 1;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  border: 2px solid var(--light-gray);
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.8);
  
  &:focus {
    outline: none;
    border-color: var(--primary-blue);
    box-shadow: 0 0 0 3px rgba(0, 191, 255, 0.1);
    background: white;
    transform: translateY(-1px);
  }

  &::placeholder {
    color: transparent;
  }
  
  &:not(:placeholder-shown) + .floating-label {
    transform: translateY(-28px) scale(0.85);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: 2px solid var(--light-gray);
  border-radius: 12px;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.8);

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
    box-shadow: 0 0 0 3px rgba(0, 191, 255, 0.1);
    background: white;
    transform: translateY(-1px);
  }

  &::placeholder {
    color: transparent;
  }
  
  &:not(:placeholder-shown) + .floating-label {
    transform: translateY(-28px) scale(0.85);
  }
`;

const SubmitButton = styled.button`
  background: var(--gradient-primary);
  color: white;
  padding: 1.2rem 2.5rem;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 191, 255, 0.3);
    
    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    
    &:hover {
      transform: none;
      box-shadow: none;
      
      &::before {
        left: -100%;
      }
    }
  }
  
  .loading-spinner {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top: 2px solid white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SuccessMessage = styled.div`
  background: linear-gradient(135deg, #e8f5e8, #f0f9f0);
  color: #2e7d2e;
  padding: 1.5rem;
  border-radius: 12px;
  text-align: center;
  font-weight: 500;
  border: 1px solid #c8e6c9;
  box-shadow: 0 4px 20px rgba(76, 175, 80, 0.1);
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.5s ease-out;
  
  &::before {
    content: "✓";
    position: absolute;
    top: -10px;
    right: -10px;
    width: 40px;
    height: 40px;
    background: var(--success-green);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    font-size: 1.2rem;
    animation: ${pulse} 2s infinite;
  }
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: rgba(211, 47, 47, 0.1);
  border-radius: 6px;
  border-left: 3px solid #d32f2f;
  animation: ${fadeInUp} 0.3s ease-out;
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [focusedField, setFocusedField] = useState("");
  const [errors, setErrors] = useState({});

  const sectionRef = useRef(null);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleFocus = (fieldName) => {
    setFocusedField(fieldName);
  };

  const handleBlur = () => {
    setFocusedField("");
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });

      // Reset success message after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    }, 1500);
  };

  return (
    <ContactContainer ref={sectionRef}>
      <Section>
        <SectionTitle className={isVisible ? "animate" : ""}>
          Contact Us
        </SectionTitle>
        <SectionSubtitle className={isVisible ? "animate" : ""}>
          Get in touch with us for any inquiries about our products or business
          opportunities
        </SectionSubtitle>

        <ContactGrid>
          <ContactInfo className={isVisible ? "animate" : ""}>
            <InfoTitle>Get In Touch</InfoTitle>

            <InfoItem>
              <InfoIcon>🏢</InfoIcon>
              <InfoContent>
                <InfoLabel>Company</InfoLabel>
                <InfoText>
                  ASKK International Group of Co.
                </InfoText>
              </InfoContent>
            </InfoItem>

            <InfoItem>
              <InfoIcon>📞</InfoIcon>
              <InfoContent>
                <InfoLabel>Phone Numbers</InfoLabel>
                <InfoText>
                  <a href="tel:+919284347826">+91 9284347826</a>
                  <br />
                  <a href="tel:+918483036769">+91 8483036769</a>
                </InfoText>
              </InfoContent>
            </InfoItem>

            <InfoItem>
              <InfoIcon>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </InfoIcon>
              <InfoContent>
                <InfoLabel>Email Address</InfoLabel>
                <InfoText>
                  <a href="mailto:askkinternationalgroup@gmail.com">
                    askkinternationalgroup@gmail.com
                  </a>
                </InfoText>
              </InfoContent>
            </InfoItem>

            <InfoItem>
              <InfoIcon>🌐</InfoIcon>
              <InfoContent>
                <InfoLabel>Website</InfoLabel>
                <InfoText>
                  <a href="https://www.askkinternational.com" target="_blank" rel="noopener noreferrer">
                    www.askkinternational.com
                  </a>
                </InfoText>
              </InfoContent>
            </InfoItem>

            <InfoItem>
              <InfoIcon>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 10C21 17 12 23 12 23S3 17 3 10C3 5.02944 7.02944 1 12 1C16.9706 1 21 5.02944 21 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </InfoIcon>
              <InfoContent>
                <InfoLabel>Office Address</InfoLabel>
                <InfoText>
                  71 Sumedh Nagar, Nagpur-26
                </InfoText>
              </InfoContent>
            </InfoItem>
          </ContactInfo>

          <ContactForm className={isVisible ? "animate" : ""}>
            <FormTitle>Send us a Message</FormTitle>

            {isSubmitted && (
              <SuccessMessage>
                Thank you for your message! We'll get back to you within 24
                hours.
              </SuccessMessage>
            )}

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <InputWrapper
                  className={`${focusedField === "name" ? "focused" : ""} ${formData.name ? "has-value" : ""}`}
                >
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onFocus={() => handleFocus("name")}
                    onBlur={handleBlur}
                    placeholder="Enter your full name"
                    required
                  />
                  <FloatingLabel htmlFor="name" className="floating-label">
                    Full Name *
                  </FloatingLabel>
                </InputWrapper>
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <InputWrapper
                  className={`${focusedField === "email" ? "focused" : ""} ${formData.email ? "has-value" : ""}`}
                >
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={() => handleFocus("email")}
                    onBlur={handleBlur}
                    placeholder="Enter your email address"
                    required
                  />
                  <FloatingLabel htmlFor="email" className="floating-label">
                    Email Address *
                  </FloatingLabel>
                </InputWrapper>
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <InputWrapper
                  className={`${focusedField === "phone" ? "focused" : ""} ${formData.phone ? "has-value" : ""}`}
                >
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    onFocus={() => handleFocus("phone")}
                    onBlur={handleBlur}
                    placeholder="Enter your phone number"
                  />
                  <FloatingLabel htmlFor="phone" className="floating-label">
                    Phone Number
                  </FloatingLabel>
                </InputWrapper>
              </FormGroup>

              <FormGroup>
                <InputWrapper
                  className={`${focusedField === "subject" ? "focused" : ""} ${formData.subject ? "has-value" : ""}`}
                >
                  <Input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    onFocus={() => handleFocus("subject")}
                    onBlur={handleBlur}
                    placeholder="What is this regarding?"
                    required
                  />
                  <FloatingLabel htmlFor="subject" className="floating-label">
                    Subject *
                  </FloatingLabel>
                </InputWrapper>
                {errors.subject && <ErrorMessage>{errors.subject}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <InputWrapper
                  className={`${focusedField === "message" ? "focused" : ""} ${formData.message ? "has-value" : ""}`}
                >
                  <TextArea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => handleFocus("message")}
                    onBlur={handleBlur}
                    placeholder="Tell us more about your inquiry..."
                    required
                  />
                  <FloatingLabel htmlFor="message" className="floating-label">
                    Message *
                  </FloatingLabel>
                </InputWrapper>
                {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
              </FormGroup>

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <div className="loading-spinner"></div>
                    Sending...
                  </>
                ) : (
                  "Send Message"
                )}
              </SubmitButton>
            </Form>
          </ContactForm>
        </ContactGrid>
      </Section>
    </ContactContainer>
  );
};

export default Contact;
